import React, { useState, useEffect } from "react";
import {
  Package,
  Calendar,
  MapPin,
  User,
  Mail,
  Phone,
  CreditCard,
  Filter,
  Download,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Ban,
  DollarSign,
  QrCode,
  Eye,
  Users,
  UserCheck,
} from "lucide-react";
import { useParams, useLocation } from "react-router-dom";
import { api } from "../../services/api.js";

const Orders = () => {
  const { eventId } = useParams();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    status: "all",
    gender: "all",
    dateRange: "all",
    startDate: "",
    endDate: "",
  });
  const [customDateRange, setCustomDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [eventDetails, setEventDetails] = useState(
    location.state?.event || null,
  );

  const fetchOrders = async (page = 1, status = "") => {
    setLoading(true);
    try {
      const queryParams = {
        page,
        limit: 10,
        eventId,
      };

      if (status) {
        queryParams.status = status;
      }

      const response = await api.getOrdersByEvent(queryParams);

      if (response.success) {
        setOrders(response.orders);
        setPagination(response.pagination);

        if (
          !eventDetails &&
          response.orders.length > 0 &&
          response.orders[0].eventId
        ) {
          setEventDetails(response.orders[0].eventId);
        }
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) {
      fetchOrders(1, filters.status === "all" ? "" : filters.status);
    }
  }, [eventId]);

  const handlePageChange = (newPage) => {
    fetchOrders(newPage, filters.status === "all" ? "" : filters.status);
  };

  const handleStatusChange = (status) => {
    setFilters((prev) => ({
      ...prev,
      status,
    }));
    fetchOrders(1, status === "all" ? "" : status);
  };

  const handleGenderChange = (gender) => {
    setFilters((prev) => ({
      ...prev,
      gender,
    }));
  };

  const handleDateRangeChange = (dateRange) => {
    setFilters((prev) => ({
      ...prev,
      dateRange,
      startDate: dateRange === "custom" ? prev.startDate : "",
      endDate: dateRange === "custom" ? prev.endDate : "",
    }));

    if (dateRange !== "custom") {
      setCustomDateRange({ startDate: "", endDate: "" });
    }
  };

  const applyCustomDateRange = () => {
    setFilters((prev) => ({
      ...prev,
      startDate: customDateRange.startDate,
      endDate: customDateRange.endDate,
    }));
  };

  const toTitleCase = (value) =>
    value
      .split(" ")
      .map((word) =>
        word ? word.charAt(0).toUpperCase() + word.slice(1) : word,
      )
      .join(" ");

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle2 },
      pending: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Clock },
      failed: { bg: "bg-red-100", text: "text-red-700", icon: XCircle },
      refunded: { bg: "bg-blue-100", text: "text-blue-700", icon: RefreshCw },
      cancelled: { bg: "bg-gray-100", text: "text-gray-700", icon: Ban },
      expired: { bg: "bg-orange-100", text: "text-orange-700", icon: Clock },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-semibold ${config.bg} ${config.text}`}
      >
        <Icon className="h-3 w-3" />
        {status.toUpperCase()}
      </span>
    );
  };

  const filteredOrders = orders.filter((order) => {
    const genderMatch =
      filters.gender === "all" ||
      (order.gender || "").toLowerCase() === filters.gender;

    if (!genderMatch) {
      return false;
    }

    if (filters.dateRange === "all") {
      return true;
    }

    const orderDate = new Date(order.timestamp);
    const today = new Date();

    if (filters.dateRange === "today") {
      return (
        orderDate.getFullYear() === today.getFullYear() &&
        orderDate.getMonth() === today.getMonth() &&
        orderDate.getDate() === today.getDate()
      );
    }

    if (filters.dateRange === "this-week") {
      const startOfWeek = new Date(today);
      startOfWeek.setHours(0, 0, 0, 0);
      startOfWeek.setDate(today.getDate() - today.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 7);
      return orderDate >= startOfWeek && orderDate < endOfWeek;
    }

    if (filters.dateRange === "this-month") {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const startOfNextMonth = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        1,
      );
      return orderDate >= startOfMonth && orderDate < startOfNextMonth;
    }

    if (filters.dateRange === "custom") {
      if (!filters.startDate || !filters.endDate) {
        return true;
      }
      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return orderDate >= start && orderDate <= end;
    }

    return true;
  });

  const stats = {
    total: filteredOrders.length,
    paid: filteredOrders.filter((o) => o.status === "paid").length,
    pending: filteredOrders.filter((o) => o.status === "pending").length,
    revenue: filteredOrders
      .filter((o) => o.status === "paid")
      .reduce((sum, o) => sum + o.amount * o.quantity, 0),
    maleCount: filteredOrders.filter(
      (o) => o.gender?.toLowerCase() === "male",
    ).length,
    femaleCount: filteredOrders.filter(
      (o) => o.gender?.toLowerCase() === "female",
    ).length,
  };

  const handleExport = () => {
    const csvContent = [
      [
        "Order ID",
        "Name",
        "Email",
        "Phone",
        "Status",
        "Amount",
        "Quantity",
        "Date",
      ],
      ...orders.map((order) => [
        order.orderId,
        order.fullName,
        order.email,
        order.phone,
        order.status,
        `${order.currency} ${order.amount}`,
        order.quantity,
        new Date(order.timestamp).toLocaleString(),
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders-${eventId}-${Date.now()}.csv`;
    a.click();
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="relative">
          <div className="h-12 w-12 sm:h-16 sm:w-16 animate-spin rounded-full border-4 border-[#F4A696] border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full h-[calc(100dvh-60px)]  flex-col gap-1 sm:gap-2  ">
      {/* Header */}
      <div className="flex flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 justify-between items-center w-full ">
        <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-[#1B3C5C]">
          Event Orders
        </h1>
        <button
          onClick={handleExport}
          disabled={orders.length === 0}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#F4A696] to-[#E89580] px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-white hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Event Details Card */}
      {eventDetails && (
        <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4">
          <div className="flex gap-2 sm:gap-3">
            {/* Thumbnail */}
            <div className="flex-shrink-0">
              {eventDetails.thumbnail ? (
                <img
                  src={eventDetails.thumbnail}
                  alt={eventDetails.title}
                  className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-lg object-cover"
                />
              ) : (
                <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-lg bg-gradient-to-br from-[#FFF4F0] to-[#FFE5DC] flex items-center justify-center">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-[#F4A696] opacity-50" />
                </div>
              )}
            </div>

            {/* Event Info */}
            <div className="flex-1 min-w-0">
              <h2 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 mb-1 sm:mb-2 line-clamp-2 sm:line-clamp-1">
                {eventDetails.title}
              </h2>
              <div className="flex flex-col gap-0 sm:gap-1">
                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600">
                  <Calendar className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="truncate">
                    {new Date(eventDetails.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                    {eventDetails.time && ` • ${eventDetails.time}`}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600">
                  <MapPin className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="truncate">
                    {eventDetails.location ||
                      `${eventDetails.city}, ${eventDetails.state}`}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards - Flexbox Layout */}
      <div className="flex flex-wrap gap-2 sm:gap-3">
        
        <div className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-[calc(33.333%-0.5rem)] lg:min-w-[calc(16.666%-0.625rem)] bg-white rounded-lg sm:rounded-xl border border-gray-200 p-3 sm:p-4 shadow-sm">
          <div className="flex items-center justify-between mb-1 sm:mb-2">
            <p className="text-[10px] sm:text-xs text-gray-600 font-medium">
              Total
            </p>
            <div className="rounded-full bg-blue-100 p-1 sm:p-1.5 md:p-2">
              <Package className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-blue-600" />
            </div>
          </div>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1B3C5C]">
            {stats.total}
          </p>
        </div>


        <div className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-[calc(33.333%-0.5rem)] lg:min-w-[calc(16.666%-0.625rem)] bg-white rounded-lg sm:rounded-xl border border-gray-200 p-3 sm:p-4 shadow-sm">
          <div className="flex items-center justify-between mb-1 sm:mb-2">
            <p className="text-[10px] sm:text-xs text-gray-600 font-medium">
              Paid
            </p>
            <div className="rounded-full bg-green-100 p-1 sm:p-1.5 md:p-2">
              <CheckCircle2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-green-600" />
            </div>
          </div>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600">
            {stats.paid}
          </p>
        </div>


        <div className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-[calc(33.333%-0.5rem)] lg:min-w-[calc(16.666%-0.625rem)] bg-white rounded-lg sm:rounded-xl border border-gray-200 p-3 sm:p-4 shadow-sm">
          <div className="flex items-center justify-between mb-1 sm:mb-2">
            <p className="text-[10px] sm:text-xs text-gray-600 font-medium">
              Pending
            </p>
            <div className="rounded-full bg-yellow-100 p-1 sm:p-1.5 md:p-2">
              <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-yellow-600" />
            </div>
          </div>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-600">
            {stats.pending}
          </p>
        </div>


        <div className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-[calc(33.333%-0.5rem)] lg:min-w-[calc(16.666%-0.625rem)] bg-white rounded-lg sm:rounded-xl border border-gray-200 p-3 sm:p-4 shadow-sm">
          <div className="flex items-center justify-between mb-1 sm:mb-2">
            <p className="text-[10px] sm:text-xs text-gray-600 font-medium">
              Revenue
            </p>
            <div className="rounded-full bg-[#FFF4F0] p-1 sm:p-1.5 md:p-2">
              <DollarSign className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-[#F4A696]" />
            </div>
          </div>
          <p className="text-base sm:text-xl md:text-2xl font-bold text-[#F4A696]">
            ₹
            {stats.revenue > 999
              ? (stats.revenue / 1000).toFixed(1) + "k"
              : stats.revenue}
          </p>
        </div>

  
        <div className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-[calc(33.333%-0.5rem)] lg:min-w-[calc(16.666%-0.625rem)] bg-white rounded-lg sm:rounded-xl border border-gray-200 p-3 sm:p-4 shadow-sm">
          <div className="flex items-center justify-between mb-1 sm:mb-2">
            <p className="text-[10px] sm:text-xs text-gray-600 font-medium">
              Male
            </p>
            <div className="rounded-full bg-blue-100 p-1 sm:p-1.5 md:p-2">
              <Users className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-blue-600" />
            </div>
          </div>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600">
            {stats.maleCount}
          </p>
        </div>

        <div className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-[calc(33.333%-0.5rem)] lg:min-w-[calc(16.666%-0.625rem)] bg-white rounded-lg sm:rounded-xl border border-gray-200 p-3 sm:p-4 shadow-sm">
          <div className="flex items-center justify-between mb-1 sm:mb-2">
            <p className="text-[10px] sm:text-xs text-gray-600 font-medium">
              Female
            </p>
            <div className="rounded-full bg-pink-100 p-1 sm:p-1.5 md:p-2">
              <UserCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-pink-600" />
            </div>
          </div>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold text-pink-600">
            {stats.femaleCount}
          </p>
        </div>
      </div>

      {/* Filters - Scrollable Container */}
      {/* <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 shadow-sm">
        <div className="overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] ">
          <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 min-w-max">
            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600" />
              <span className="text-xs sm:text-sm font-medium text-gray-700">
                Filter:
              </span>
            </div>
            <div className="flex gap-1.5 sm:gap-2">
              <button
                onClick={() => handleFilterChange("")}
                className={`flex-shrink-0 px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-lg text-xs font-medium transition-colors ${
                  filters.status === ""
                    ? "bg-[#F4A696] text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                All
              </button>
              {["paid", "pending", "failed", "cancelled", "refunded"].map(
                (status) => (
                  <button
                    key={status}
                    onClick={() => handleFilterChange(status)}
                    className={`flex-shrink-0 px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-lg text-xs font-medium capitalize transition-colors ${
                      filters.status === status
                        ? "bg-[#F4A696] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {status}
                  </button>
                ),
              )}
            </div>
          </div>
        </div>
      </div> */}

      <div className="flex items-center gap-2 overflow-auto px-2 py-6 w-[390px] md:w-full bg-white rounded-lg sm:rounded-xl border border-gray-200 shadow-sm scrollbar-none">
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600" />
          <span className="text-xs sm:text-sm font-medium text-gray-700">
            Filter:
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <label className="text-xs sm:text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-gray-100 text-gray-700 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F4A696]/40"
            >
              {[
                "all",
                "pending",
                "paid",
                "failed",
                "refunded",
                "cancelled",
                "expired",
              ].map((status) => (
                <option key={status} value={status}>
                  {toTitleCase(status)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <label className="text-xs sm:text-sm font-medium text-gray-700">
              Gender
            </label>
            <select
              value={filters.gender}
              onChange={(e) => handleGenderChange(e.target.value)}
              className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-gray-100 text-gray-700 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F4A696]/40"
            >
              {["all", "male", "female"].map((gender) => (
                <option key={gender} value={gender}>
                  {toTitleCase(gender)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <label className="text-xs sm:text-sm font-medium text-gray-700">
              Date
            </label>
            <select
              value={filters.dateRange}
              onChange={(e) => handleDateRangeChange(e.target.value)}
              className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-gray-100 text-gray-700 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F4A696]/40"
            >
              <option value="all">All</option>
              <option value="today">Today</option>
              <option value="this-week">This Week</option>
              <option value="this-month">This Month</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {filters.dateRange === "custom" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customDateRange.startDate}
                onChange={(e) =>
                  setCustomDateRange((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
                className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-gray-100 text-gray-700 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F4A696]/40"
              />
              <input
                type="date"
                value={customDateRange.endDate}
                onChange={(e) =>
                  setCustomDateRange((prev) => ({
                    ...prev,
                    endDate: e.target.value,
                  }))
                }
                className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-gray-100 text-gray-700 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F4A696]/40"
              />
              <button
                type="button"
                onClick={applyCustomDateRange}
                className="flex-shrink-0 px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 rounded-lg text-xs font-medium transition-colors bg-[#F4A696] text-white hover:bg-[#E89580]"
              >
                Find
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Orders Table - ONLY Container with Scroll */}
      <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 shadow-sm ">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#F4A696] border-t-transparent"></div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-12">
            <Package className="h-12 w-12 text-gray-400 mb-3" />
            <p className="text-gray-500 font-medium">No orders found</p>
          </div>
        ) : (
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 w-[390px] md:w-full ">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    Order Details
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    Customer
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    Gender
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    Payment
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    Status
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    Date
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr
                    key={order._id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                      <div className="flex flex-col gap-1">
                        <p className="text-xs sm:text-sm font-semibold text-gray-900 whitespace-nowrap">
                          {order.orderId}
                        </p>
                        <p className="text-xs text-gray-500 whitespace-nowrap">
                          Qty: {order.quantity}
                        </p>
                        {order.isGuestBooking && (
                          <span className="inline-flex self-start items-center rounded-md bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 whitespace-nowrap">
                            Guest
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                      <div className="flex flex-col gap-1 min-w-[180px]">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 flex items-center gap-1">
                          <User className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate max-w-[150px]">
                            {order.fullName}
                          </span>
                        </p>
                        <p className="text-xs text-gray-600 flex items-center gap-1">
                          <Mail className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate max-w-[150px]">
                            {order.email}
                          </span>
                        </p>
                        <p className="text-xs text-gray-600 flex items-center gap-1">
                          <Phone className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 flex-shrink-0" />
                          <span className="whitespace-nowrap">
                            {order.phone}
                          </span>
                        </p>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                      <p className="text-xs sm:text-sm font-semibold text-gray-900 capitalize whitespace-nowrap">
                        {order.gender || "N/A"}
                      </p>
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                      <div className="flex flex-col gap-1 min-w-[120px]">
                        <p className="text-xs sm:text-sm font-bold text-green-600 whitespace-nowrap">
                          {order.currency}{" "}
                          {(order.amount * order.quantity).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500 capitalize flex items-center gap-1 whitespace-nowrap">
                          <CreditCard className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
                          {order.paymentMethod}
                        </p>
                        {order.transactionId && (
                          <p
                            className="text-xs text-gray-500 truncate max-w-[120px]"
                            title={order.transactionId}
                          >
                            {order.transactionId}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                      <div className="flex flex-col gap-2 min-w-[100px]">
                        {getStatusBadge(order.status)}
                        {order.scanned && (
                          <span className="flex items-center gap-1 text-xs text-green-600 font-medium whitespace-nowrap">
                            <QrCode className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            Scanned
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                      <div className="flex flex-col gap-1 min-w-[100px]">
                        <p className="text-xs sm:text-sm text-gray-900 whitespace-nowrap">
                          {new Date(order.timestamp).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            },
                          )}
                        </p>
                        <p className="text-xs text-gray-500 whitespace-nowrap">
                          {new Date(order.timestamp).toLocaleTimeString(
                            "en-US",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}
                        </p>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                      <button
                        onClick={() => alert(`View order: ${order.orderId}`)}
                        className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-100 transition-colors whitespace-nowrap"
                      >
                        <Eye className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                        <span className="hidden sm:inline">View</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 bg-white rounded-lg sm:rounded-xl border border-gray-200 px-3 sm:px-4 py-2 sm:py-3 shadow-sm">
          <p className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
            <span className="font-medium">
              {(pagination.page - 1) * 10 + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(pagination.page * 10, pagination.total)}
            </span>{" "}
            of <span className="font-medium">{pagination.total}</span>
          </p>
          <div className="flex items-center gap-1 sm:gap-2 justify-center">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="flex-shrink-0 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Prev
            </button>
            {[...Array(Math.min(pagination.totalPages, 3))].map((_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`flex-shrink-0 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    pagination.page === pageNum
                      ? "bg-[#F4A696] text-white"
                      : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="flex-shrink-0 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add Tailwind scrollbar utilities to your global CSS */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-thin::-webkit-scrollbar {
          height: 8px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export { Orders };
